import * as React from "react";
import { useLocation } from "wouter";
import { Home, Package, MapPin, Zap, Settings, LogOut, Shield, Search, MoreHorizontal, X, CalendarClock, Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext.js";
import { cn } from "@/lib/utils.js";
import { VerificationBanner } from "./VerificationBanner.js";
import { LegalLinks } from "./LegalLinks.js";
import { SearchModal, SearchTrigger } from "@/features/search/SearchModal.js";

const NAV_ITEMS = [
  { path: "/", label: "Home", icon: Home },
  { path: "/inventory", label: "Inventory", icon: Package },
  { path: "/spaces", label: "Spaces", icon: MapPin },
  { path: "/organize", label: "Organize", icon: Zap },
  { path: "/settings", label: "Settings", icon: Settings },
];

const MOBILE_PRIMARY = NAV_ITEMS.slice(0, 4);
const MOBILE_OVERFLOW = NAV_ITEMS.slice(4);

// Desktop-only sub-nav under "Organize". The hub itself is a pure status surface
// (Option C), so the launcher lives on its own — "Start something" leads, then the
// three organizing categories, grouped by nature of the work (Placement · Events ·
// Keep It Up), giving desktop power users zero-tap access to any category. Rendered
// only when the Organize section is active. The mobile bottom bar stays flat (a nav
// tree doesn't fit a 5-tab bar); mobile surfaces the launcher via a button on the hub.
const ORGANIZE_SUBNAV = [
  { path: "/organize/start", label: "Start something", icon: Plus },
  { path: "/organize/placement", label: "Placement", icon: MapPin },
  { path: "/organize/events", label: "Events", icon: CalendarClock },
  { path: "/organize/upkeep", label: "Keep it up", icon: Zap },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const [location, navigate] = useLocation();
  const { user, logout } = useAuth();
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [moreOpen, setMoreOpen] = React.useState(false);

  // Global Cmd+K shortcut
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <div className="min-h-screen bg-neutral flex">
      {/* Sidebar — desktop */}
      <aside className="hidden md:flex flex-col w-64 bg-white border-r border-neutral-border/30 p-6">
        <div className="mb-8">
          <h1 className="text-xl font-bold text-primary font-headline">Homethyst</h1>
          <p className="text-xs text-gray-500 mt-1">{user?.householdName}</p>
        </div>
        <nav className="flex-1 space-y-1" aria-label="Main navigation">
          <div className="mb-3">
            <SearchTrigger onClick={() => setSearchOpen(true)} />
          </div>
          {NAV_ITEMS.map(({ path, label, icon: Icon }) => {
            const active = path === "/" ? location === "/" : location.startsWith(path);
            const isOrganize = path === "/organize";
            return (
            <React.Fragment key={path}>
            <button onClick={() => navigate(path)} aria-current={active ? "page" : undefined} className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
              active ? "bg-amethyst-soft text-amethyst-ink" : "text-gray-500 hover:bg-gray-50",
            )}>
              <Icon className="w-5 h-5" />{label}
            </button>
            {isOrganize && active && (
              <div className="ml-4 pl-3 border-l border-neutral-border/40 space-y-0.5 mt-0.5 mb-1">
                {ORGANIZE_SUBNAV.map(({ path: subPath, label: subLabel, icon: SubIcon }) => {
                  const subActive = location.startsWith(subPath);
                  return (
                    <button key={subPath} onClick={() => navigate(subPath)} aria-current={subActive ? "page" : undefined} className={cn(
                      "w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[0.8125rem] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                      subActive ? "text-amethyst-ink bg-amethyst-soft/60" : "text-gray-500 hover:bg-gray-50",
                    )}>
                      <SubIcon className="w-4 h-4" />{subLabel}
                    </button>
                  );
                })}
              </div>
            )}
            </React.Fragment>
            );
          })}
          {user?.isAdmin && (
            <button onClick={() => navigate("/admin")} className={cn(
              "w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
              location === "/admin" ? "bg-amethyst-soft text-amethyst-ink" : "text-gray-500 hover:bg-gray-50",
            )}>
              <Shield className="w-5 h-5" />Admin
            </button>
          )}
        </nav>
        <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 text-sm text-gray-500 hover:text-gray-700 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
          <LogOut className="w-5 h-5" />Sign out
        </button>
      </aside>

      {/* Main content */}
      <main className="flex-1 pb-20 md:pb-0 flex flex-col">
        <VerificationBanner />
        <div className="max-w-5xl mx-auto w-full p-4 md:p-8 flex-1">{children}</div>
        {/* Footer — desktop only (mobile has the bottom nav bar instead) */}
        <footer className="hidden md:block border-t border-neutral-border/20 mt-8">
          <LegalLinks className="max-w-5xl mx-auto w-full px-8 py-4 justify-start" />
        </footer>
      </main>

      {/* Bottom nav — mobile */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-neutral-border/30 flex justify-around py-2 z-50" role="navigation" aria-label="Main navigation">
        {MOBILE_PRIMARY.map(({ path, label, icon: Icon }) => {
          const active = path === "/" ? location === "/" : location.startsWith(path);
          return (
          <button key={path} onClick={() => navigate(path)} aria-current={active ? "page" : undefined} className={cn(
            "flex flex-col items-center gap-0.5 px-3 py-1 text-[11px] font-medium",
            active ? "text-primary" : "text-gray-500",
          )}>
            <Icon className="w-5 h-5" />{label}
          </button>
          );
        })}
        <div className="relative">
          <button onClick={() => setMoreOpen(!moreOpen)} aria-expanded={moreOpen} aria-haspopup="true" className={cn(
            "flex flex-col items-center gap-0.5 px-3 py-1 text-[11px] font-medium",
            MOBILE_OVERFLOW.some((i) => i.path === location) ? "text-primary" : "text-gray-500",
          )}>
            <MoreHorizontal className="w-5 h-5" />More
          </button>
          {moreOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMoreOpen(false)} />
              <div className="absolute bottom-full right-0 mb-2 w-48 bg-white rounded-xl border border-neutral-border/30 shadow-lg z-50 py-1" role="menu">
                {MOBILE_OVERFLOW.map(({ path, label, icon: Icon }) => (
                  <button key={path} role="menuitem" onClick={() => { navigate(path); setMoreOpen(false); }} className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 text-sm font-medium",
                    location === path ? "bg-amethyst-soft text-amethyst-ink" : "text-gray-600 hover:bg-gray-50",
                  )}>
                    <Icon className="w-4 h-4" />{label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </nav>

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}
